import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import type { CheckUserBusinessMapResponse } from "@/types/draftCalendar";

interface SubmitCalendarDialogProps {
  open: boolean;
  loading: boolean;
  results: CheckUserBusinessMapResponse[];
  isConfigured: boolean;
  submitting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function SubmitCalendarDialog({
  open,
  loading,
  results,
  isConfigured,
  submitting,
  onConfirm,
  onClose,
}: SubmitCalendarDialogProps) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onClose}
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
      tabIndex={-1}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="submit-calendar-title"
        className="w-full max-w-lg border border-slate-300 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 id="submit-calendar-title" className="text-base font-semibold text-slate-900">
            Submit Calendar
          </h2>
        </div>

        {/* Body */}
        <div className="px-4 py-4 text-sm text-slate-700" style={{ minHeight: 120 }}>
          {loading ? (
            <div className="flex items-center justify-center py-8 gap-2 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Checking business configurations&hellip;
            </div>
          ) : results.length === 0 ? (
            <p className="text-slate-500">No business/meeting type combinations found.</p>
          ) : (
            <>
              <p className="mb-3 text-slate-600">
                {isConfigured
                  ? "All businesses are configured. You may proceed with submitting the calendar."
                  : "Some businesses are not configured. Please resolve the issues before submitting."}
              </p>
              <div className="divide-y divide-slate-100 border border-slate-200">
                <div className="grid grid-cols-2 bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700">
                  <span>Business</span>
                  <span>Status</span>
                </div>
                {results.map((r, i) => (
                  <div key={i} className="grid grid-cols-2 px-3 py-2 text-xs">
                    <span className="truncate" title={r.business}>{r.business}</span>
                    <span className="flex items-center gap-1">
                      {r.isConfigured?.toLowerCase() === "configured" ? (
                        <>
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                          <span className="text-green-700">Configured</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-3.5 w-3.5 text-red-600" />
                          <span className="text-red-700">{r.isConfigured || "Not Configured"}</span>
                        </>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading || !isConfigured || submitting}
            className="border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <span className="flex items-center gap-1"><Loader2 className="h-4 w-4 animate-spin" />Submitting&hellip;</span>
            ) : "Submit Calendar"}
          </button>
        </div>
      </div>
    </div>
  );
}
